import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { Check, Award, ArrowLeft } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'

interface ProductHeroSectionProps {
  product: {
    name: string
    tagline: string
    description: string
    features: string[]
    certifications: string[]
    image: string
  }
  category: string
}

export function ProductHeroSection({ product }: ProductHeroSectionProps) {
  const { t } = useTranslation('common')

  return (
    <section className="relative overflow-hidden bg-[#FDF8F3] pt-28 md:pt-36 pb-16 md:pb-24">
      {/* Grain */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        }}
      />

      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <motion.nav
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-10"
          aria-label={t('aria.breadcrumb')}
        >
          <ol className="flex items-center gap-2 text-sm">
            <li>
              <LocalizedLink
                to="products"
                className="flex items-center gap-1.5 text-[#4E2118]/50 hover:text-[#DF631E] transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                {t('nav.products')}
              </LocalizedLink>
            </li>
            <li className="text-[#4E2118]/25">/</li>
            <li className="text-[#4E2118] font-medium">{product.name}</li>
          </ol>
        </motion.nav>

        {/* Main grid */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div className="relative aspect-square bg-white border border-[#4E2118]/8 overflow-hidden">
              <motion.img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-cover"
                initial={{ scale: 1.05 }}
                animate={{ scale: 1 }}
                transition={{ duration: 1.2, ease: 'easeOut' }}
              />
            </div>

            {/* Certifications */}
            {product.certifications.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="flex flex-wrap gap-2 mt-4"
              >
                {product.certifications.map((cert) => (
                  <div key={cert} className="flex items-center gap-2 px-3 py-1.5 border border-[#4E2118]/10 bg-white text-xs font-medium text-[#4E2118]">
                    {cert === 'dop' ? (
                      <Award className="w-3.5 h-3.5 text-[#DF631E]" />
                    ) : cert === 'senza-glutine' ? (
                      <img src="/images/senza-glutine.svg" alt="" className="w-4 h-4" />
                    ) : null}
                    {cert === 'dop' ? 'DOP' : t(`productDetail.certifications.glutenFree`)}
                  </div>
                ))}
              </motion.div>
            )}
          </motion.div>

          {/* Info */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="space-y-6"
          >
            <div className="flex items-center gap-4">
              <span className="h-px w-10 bg-[#DF631E]" />
              <span className="text-xs font-semibold uppercase tracking-[0.25em] text-[#DF631E]">
                {product.tagline}
              </span>
            </div>

            <h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-[#4E2118] leading-[1.05]"
              style={{ fontFamily: 'Playfair Display, Georgia, serif' }}
            >
              {product.name}
            </h1>

            <p className="text-base md:text-lg text-[#4E2118]/60 leading-relaxed max-w-lg">
              {product.description}
            </p>

            {/* Features */}
            <div className="space-y-3 pt-2">
              <h2 className="text-xs font-semibold text-[#4E2118]/40 uppercase tracking-[0.2em]">
                {t('productDetail.features.title')}
              </h2>
              <ul className="grid sm:grid-cols-2 gap-2.5">
                {product.features.map((feature) => (
                  <li key={feature} className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-5 h-5 border border-[#DF631E]/20 flex items-center justify-center">
                      <Check className="w-3 h-3 text-[#DF631E]" />
                    </div>
                    <span className="text-sm text-[#4E2118]/80">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* CTAs */}
            <div className="flex flex-wrap gap-4 pt-4">
              <a
                href="#product-details"
                className="inline-flex items-center px-7 py-3.5 bg-[#DF631E] text-white text-sm font-semibold tracking-wide uppercase hover:bg-[#B85018] transition-colors duration-300"
              >
                {t('productDetail.cta.discoverMore')}
              </a>
              <LocalizedLink
                to="recipes"
                className="inline-flex items-center px-7 py-3.5 border border-[#4E2118]/20 text-[#4E2118] text-sm font-semibold tracking-wide uppercase hover:bg-[#4E2118]/5 transition-colors duration-300"
              >
                {t('productDetail.cta.viewRecipes')}
              </LocalizedLink>
            </div>
          </motion.div>
        </div>
      </div>

    </section>
  )
}
